import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ValidationMessageComponent } from '../../../shared/components/validation-message/validation-message.component';
import { RoutesPath } from '../../../shared/constants/routes-path';
import { Router } from '@angular/router';
import { SharedService } from '../../../shared/services/shared.service';
import { AccountModel } from '../../../shared/models/loginuser.model';
import { Utils } from '../../../Utils/utils';
import { ApplicationRequest } from '../../../shared/models/application-request.model';
import { SrStatus } from '../../../shared/constants/status.enum';
import { FieldValue, ServiceName, UserType } from '../../../shared/constants/services.enum';
import { ApiEndPoints, ApiRoot } from '../../../shared/constants/api-endpoints';
import { ApiService } from '../../../core/services/api.service';
import { SkeletonLoaderComponent } from "../../../shared/components/skeleton-loader/skeleton-loader.component";
import { DcMethodValues } from '../../../shared/constants/dc-method.constants';

@Component({
  selector: 'app-account-management',
  imports: [CommonModule, TranslateModule, ButtonComponent, ValidationMessageComponent, SkeletonLoaderComponent],
  templateUrl: './account-management.component.html',
  styleUrl: './account-management.component.scss'
})
export class AccountManagementComponent {

  applicationRequestPayload: ApplicationRequest = new ApplicationRequest();
  memberships: AccountModel[] = [];
  showAddBusinessMessage = false;
  lan: string = 'en';
  accountCSN: string = '';
  accountId: string = '';
  invalidMembershipAccountId: string | null = null;
  constructor(
    private router: Router,
    private sharedService: SharedService,
    private api: ApiService
  ) {
    this.lan = this.sharedService.getCurrentLang();
    this.accountCSN = this.sharedService.getCsnAccountId('csn');
    this.accountId = this.sharedService.getCsnAccountId('id');
    this.getListOfAccounts();
    this.getActiveMembership();
    this.getPendingAccessReqSR();
  }

  // This method is used to check if license active using m
  isLicenseActive(m: any): boolean {
    let today = new Date();
    if (new Date(m?.['DC Expiry Date']) > today) {
      return true;
    } else {
      return false;
    }
  }

  getMemberDisplayStatus(member: any): string {
    return this.sharedService.getMemberDisplayStatus(member);
  }

  accountsList: any = [];
  loadingAccounts: boolean = false;
  // This method is used to get list of accounts
  getListOfAccounts() {
    this.loadingAccounts = true;
    let requestPayload = {
      "body": {
        "ProcessName": "DC Dashboard Count API WF",
        "DC Channel": this.lan == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.ACCOUNT_MANAGEMENT
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        next: (response: any) => {
          if (response["Error Code"] == "" && response["Error Message"] == "" && response?.SiebelMessage) {
            this.loadingAccounts = false;
            if (response?.SiebelMessage['DC User Thin']?.['DC Account Count BC']) {
              const accounts = Utils.checkIsArray(response?.SiebelMessage['DC User Thin']?.['DC Account Count BC']);
              this.accountsList = this.sortAccountsByExpiryDateAsc(accounts);
            }
          } else {
            this.loadingAccounts = false;
          }
        }
      })
  }

  // This method is used to sort accounts by expiry date in ascending order
  private sortAccountsByExpiryDateAsc(accounts: any[]): any[] {
    return [...(accounts || [])].sort((a: any, b: any) => {
      const aTime = this.getExpiryDateSortTime(a?.['DC Expiry Date']);
      const bTime = this.getExpiryDateSortTime(b?.['DC Expiry Date']);
      return aTime - bTime;
    });
  }

  // This method is used to get sortable timestamp from expiry date
  private getExpiryDateSortTime(value: unknown): number {
    const parsed = Utils.parseExpiryDate(value);
    if (!parsed) {
      return Number.MAX_SAFE_INTEGER;
    }
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
  }

  isPrimaryUser(csn: string) {
    return this.sharedService.checkPrimaryUser(csn);
  }
  // This method is used to download cert
  downloadCert(member: AccountModel) {
    if (member['DC Certificate URL']) {
      this.openDownloadLink(member['DC Certificate URL']);
    }
  }
  // This method is used to manage users
  manageUsers(member: AccountModel) {
    if (this.setAsPrimaryAccount(member)) {
      this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.MANAGE_SIGNATORIES}`]);
    }
  }

  // This method is used to apply service
  applyService() {
    this.router.navigate([RoutesPath.SERVICES_LIST]);
  }
  // This method is used to apply new membership
  applyNewMembership() {
    this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.REGISTRATION}`]);
  }

  // This method is used to view membership
  viewMembership(m: any) {
    if (m?.CSN) {
      this.router.navigate([RoutesPath.MEMBERSHIP.DETAIL, m.CSN]);
    }
  }
  // This method is used to join existing
  joinExisting() {
    this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.ADD_MORE_BUSINESS}`]);
  }
  // This method is used to goto benfits
  gotoBenfits() {
    this.router.navigate([RoutesPath.PLATINUM_MEMBERSHIP_BENEFITS]);
  }

  // This method is used to get active membership
  getActiveMembership() {
    const thresholdDate = new Date();
    const userDetails: any = this.sharedService.getUserProfile();
    let accounts = Utils.checkIsArray(userDetails?.Account) ?? [];
    const activeMemberships: any[] = [];

    accounts?.forEach((acc: any) => {
      const csn = acc.CSN?.trim();
      const expiryStr = acc["DC Expiry Date"];

      // parse expiry date (assuming format MM/DD/YYYY)
      const [month, day, year] = expiryStr.split('/').map(Number);
      const expiryDate = new Date(year, month - 1, day);

      //Active if expiry > today + 30 days
      if (expiryDate > thresholdDate) {
        activeMemberships.push(acc);
      }
    });

    // assign to variable
    this.memberships = activeMemberships;

    const storedPrimary = this.sharedService.getPrimaryAccount();
    if (storedPrimary?.CSN) {
      this.accountCSN = storedPrimary.CSN;
      this.accountId = storedPrimary.Id;
    } else {
      const primaryAccount = accounts?.find((acc: any) => acc.CSN === userDetails?.['Primary CSN']);
      if (primaryAccount) {
        this.accountCSN = primaryAccount.CSN;
        this.accountId = primaryAccount.Id;
        this.sharedService.setPrimaryAccount(primaryAccount);
      } else if (activeMemberships.length) {
        this.accountCSN = activeMemberships[0].CSN;
        this.accountId = activeMemberships[0].Id;
        this.sharedService.setPrimaryAccount(activeMemberships[0]);
      }
    }
  }

  pendingRequests: any[] = [];
  // This method is used to get pending applications/requests
  getPendingAccessReqSR() {
    let userLoginName = this.sharedService.getUserProfile()['Login Name'];
    this.applicationRequestPayload.body.IncomingHierarchy.MessageId = Utils.generateUniqueId();

    this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["Account Id"] = '';
    this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"].CSN = '';
    this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["SR Number"] = '';
    this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["SR Type"] = ServiceName.USER_MANAGEMENT;
    this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["SR Sub Type"] = ServiceName.USER_ACCESS;
    this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]["Status"] = SrStatus.InProgress;
    this.applicationRequestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO["Service Request"]['Created By Name'] = userLoginName;
    this.applicationRequestPayload.body.PageSize = 50;
    this.applicationRequestPayload.body.StartRowNum = 0;
    this.applicationRequestPayload.body.NewQuery = true;
    this.applicationRequestPayload.body.NoDays = 50;
    this.pendingRequests = [];
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.GET_SR_SUMMARY, this.applicationRequestPayload)
      .subscribe({
        next: (response: any) => {
          if (response?.SiebelMessage) {
            if (response?.SiebelMessage['Service Request']) {
              this.pendingRequests = Utils.checkIsArray(response?.SiebelMessage['Service Request']);
            }
          }
        }
      })
  }

  // This method is used to set primary account using member
  setAsPrimaryAccount(account: AccountModel): boolean {
    const userDetails: any = this.sharedService.getUserProfile();
    let accounts = Utils.checkIsArray(userDetails?.Account) ?? [];
    let member: any = "";
    if (accounts) {
      member = accounts.find((m: any) => m.Id == account.Id || m.CSN == account.CSN);
    }
    if (member) {
      const isMemberAccount = member?.['Account Type Code'] === UserType.MEMBER || member?.['Account Type Code'] === UserType.MEMBER_AR;
      this.invalidMembershipAccountId = null;
      if (isMemberAccount && !member?.CSN) {
        this.invalidMembershipAccountId = member?.Id ?? null;
      }

      this.sharedService.setPrimaryAccount(member);
      this.accountCSN = member.CSN;
      this.accountId = member.Id;
      this.UpdatePrimaryCSN(member);
      return true;
    } else {
      this.router.navigate([RoutesPath.DASHBOARD]);
      return false;
    }
  }

  // This method is used to update primary CSN in DC system
  UpdatePrimaryCSN(member: AccountModel) {
    if (!member.CSN) {
      return;
    }
    const requestPayload = {
      "body": {
        "ProcessName": 'DC Update User Primay Account CSN',
        "Account Id": member.Id,
        "DC Channel": this.lan == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": "Set Primary"
      }
    }
    this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.RUN_PROCESS,
      requestPayload
    ).subscribe({
      next: (response: any) => {
        if (response['Error Code'] == "" && response['Error Message'] == "") {
          this.sharedService.toastMessage('SUCCESS', 'PRIMARY_ACCOUNT_UPDATED_SUCCESSFULLY', 'success');
        }
      },
      error: () => { }
    })
  }

  // This method is used to open download link using url
  openDownloadLink(url: any): void {
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  // This method is used to revoke the pending request
  revokeRequest(m: any) {
    const srNumber = m['SR Number'];
    if (!srNumber) return;
    let requestPayload = {
      "body": {
        "ProcessName": "DC Revoke Pending Request WF",
        "DC Channel": this.lan == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": DcMethodValues.ACCOUNT_MANAGEMENT,
        "SR Number": srNumber
      }
    }
    this.api.post(ApiRoot.DCCICPIntegration, ApiEndPoints.RUN_PROCESS, requestPayload)
      .subscribe({
        next: (response: any) => {
          if (response['Error Code'] == "" && response['Error Message'] == "") {
            this.pendingRequests = this.pendingRequests?.filter(r => r['SR Number'] !== srNumber);
            this.sharedService.toastMessage('Success', 'SUCCESS', 'success');
          } else {
            this.sharedService.toastMessage('Error', 'TECHNICAL_ERROR', 'error');
          }
        },
        error: () => {
        }
      })
  }
  getNumberValue(value: any): number {
    let parsed: number;

    if (typeof value === 'number') {
      parsed = value;
    } else if (typeof value === 'string') {
      parsed = parseFloat(value);
    } else {
      return 0;
    }

    return isNaN(parsed) ? 0 : Math.trunc(parsed);
  }
}
